import { useEffect, useMemo, useRef } from 'react';
import useNavigationStore from 'src/store/navigation';
import type { NavigationPane } from './DesktopDrawer/nav-group-tab-shortcuts';
import { isPathActive } from './DesktopDrawer/utils';
import type { NavigationDataItem } from './use-navigation-items';

export type NavigationGroup = { items: NavigationDataItem[] };

// not evicted by a late routePane recompute without an actual navigation
const LATE_RECOMPUTE_SAFE_PANES: NavigationPane[] = ['search', 'inbox', 'sidekick'];

// home-owned entity routes are navigated from inside these, so only an explicit
// tab click or a settings route may switch away
const WORKFLOW_PANES: NavigationPane[] = ['inbox', 'sidekick'];

/**
 * The pane owning the current route wins over manual tab selection, so the
 * parent grouping button auto-activates on URL entry, deep links and
 * in-portal links — not only on nav clicks. Routes owned by neither pane
 * keep the last selected pane.
 */
export const useRoutePane = ({
  navigationGroups,
  settingsNavigationGroups,
  pathname,
}: {
  navigationGroups: NavigationGroup[];
  settingsNavigationGroups: NavigationGroup[];
  pathname: string;
}): NavigationPane | null =>
  useMemo(() => {
    const isItemActive = (item: NavigationDataItem) =>
      isPathActive({
        pathTemplate: item.pathTemplate,
        path: item.path,
        locationPath: pathname,
      });

    if (navigationGroups.some((group) => group.items.some(isItemActive))) {
      return 'home';
    }

    if (settingsNavigationGroups.some((group) => group.items.some(isItemActive))) {
      return 'settings';
    }

    return null;
  }, [navigationGroups, settingsNavigationGroups, pathname]);

export const usePaneRouteSync = ({
  routePane,
  pathname,
}: {
  routePane: NavigationPane | null;
  pathname: string;
}) => {
  const setActivePane = useNavigationStore((state) => state.setActivePane);
  const prevPathnameRef = useRef<string | null>(null);

  useEffect(() => {
    const pathChanged = prevPathnameRef.current !== pathname;
    prevPathnameRef.current = pathname;

    // a redirect that also wants a pane open (e.g. /inbox) requests it here so
    // the route change itself can't race the pane switch
    const { pendingPane, setPendingPane } = useNavigationStore.getState();
    if (pendingPane) {
      setPendingPane(null);
      setActivePane(pendingPane);
      return;
    }

    if (!routePane) {
      return;
    }

    // read the pane imperatively so this effect only reacts to route changes —
    // depending on activePane would let it overwrite a manual pane click (e.g.
    // opening Settings while on a Home route) right after the user makes it
    const currentPane = useNavigationStore.getState().activePane;

    // navigation groups load async, and a late recompute is not a navigation
    if (LATE_RECOMPUTE_SAFE_PANES.includes(currentPane) && !pathChanged) {
      return;
    }

    if (WORKFLOW_PANES.includes(currentPane) && routePane === 'home') {
      return;
    }

    setActivePane(routePane);
  }, [routePane, pathname, setActivePane]);
};
